<template>
  <PageDynamic path="/our-portfolio" :title="pageTitle" :description="pageDescription">
    <h1 class="sr-only">Our Portfolio</h1>
    <section class="section">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <div
              class="section__banner text-black"
              data-aos="fade-left"
              data-aos-duration="600"
            >
              <div class="heading">OUR</div>
              <div class="heading-hightlight">PORTFOLIO</div>
            </div>
          </div>
          <div class="col-12">
            <div data-aos="fade-up" data-aos-duration="600">
              <p class="small text-gray-1">You Interesting...</p>
              <div class="portfolio-sorting">
                <!-- ปุ่ม "All" -->
                <button
                  type="button"
                  class="portfolio-sorting__item"
                  :class="{ active: activeCategory === 'All' }"
                  @click="changeCategory('All')"
                >
                  All
                </button>

                <!-- ปุ่ม Filter อื่น ๆ -->
                <button
                  v-for="(type, index) in categories"
                  :key="index"
                  type="button"
                  class="portfolio-sorting__item"
                  :class="{ active: activeCategory === type.name }"
                  @click="changeCategory(type.name)"
                >
                  {{ type.name }}
                </button>

                <!-- <Loading v-if="isLoading" /> -->
              </div>
            </div>
          </div>
          <div class="col-12">
            <div
              data-aos="fade-up"
              data-aos-duration="600"
              data-aos-delay="300"
              class="card-deck card-deck--portfolio"
              v-if="filteredPosts.length"
            >
              <!-- แสดงข้อมูล posts ที่ถูกกรอง -->
              <Portfolio
                :posts="filteredPosts"
                :isAll="true"
                class="our-portfolio"
              />
            </div>

            <div v-else>
              <p>No data for your interesting</p>
            </div>
          </div>
        </div>
      </div>
    </section>
    <SiConnect />
  </PageDynamic>
</template>

<script setup>
import debounce from "lodash/debounce";
import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import SiConnect from "@/components/apps/block/SiConnect.vue";
import Portfolio from "@/components/apps/card/Portfolio.vue";
import PageDynamic from "@/components/pages/Dynamic.vue";

import { usePageContentStore } from "@/stores/pageContent";
import { useClientServiceStore } from "@/stores/clientService";
import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";

const store = useClientServiceStore();
const storePage = usePageContentStore();
const route = useRoute();
const { public: { siteUrl } } = useRuntimeConfig();

const pageTitle = computed(() => storePage.title);
const pageDescription = computed(() => storePage.description);

const { fetchPortfolio, fetchPortfolioGroup } = useClientServiceStore();

// Independent fetches — run in parallel rather than serially. Metadata fetch
// uses the same literal path fetchPageContent uses below, so the cache key
// matches and the client-only fetch (which also parses shortcodes) reuses
// this response instead of fetching again.
await Promise.all([
  storePage.fetchPageMeta('/our-portfolio'),
  fetchPortfolio(),
  fetchPortfolioGroup(),
]);

const pageUrl = computed(() => `${siteUrl}${route.path}`);

useHead(() => {
  // Last crumb is always this page — index from the end so a future CMS
  // re-nesting under a parent can't silently swap in the ancestor's name.
  const crumbs = storePage.breadcrumbs;
  const pageName = crumbs[crumbs.length - 1]?.title?.trim() || "Our Portfolio";
  const breadcrumbItems = [
    { name: "Home", url: `${siteUrl}/` },
    { name: pageName, url: pageUrl.value },
  ];

  return {
    script: [
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify(useBreadcrumbSchema(breadcrumbItems, pageUrl.value)),
      },
    ],
  };
});


// กำหนดค่าเริ่มต้น
const activeCategory = ref("All"); // Category ที่ active เริ่มต้นที่ "All"
const isLoading = ref(false);

// ฟังก์ชันตั้งค่า active category
const setActiveCategory = debounce((category) => {
  activeCategory.value = category;
  isLoading.value = false; // ตั้งค่า isLoading เป็น false หลัง debounce เสร็จสิ้น
}, 300);

// ฟังก์ชัน wrapper สำหรับใช้งาน setActiveCategory
const changeCategory = (category) => {
  isLoading.value = true; // ตั้งค่า isLoading เป็น true ก่อนเริ่ม debounce
  setActiveCategory(category);
};

// กรองข้อมูล posts ตาม category
const { posts, categories } = storeToRefs(store);

const filteredPosts = computed(() => {
  if (activeCategory.value === "All") {
    return posts.value; // แสดงทั้งหมดถ้าเลือก "All"
  }
  return posts.value.filter(post => post.groups.find((group) => group.name === activeCategory.value));
});


// Fetch the page content based on the route's slug
const fetchPageContent = async () => {
	try {

		await storePage.fetchPageContent('/our-portfolio');

	} catch (error) {
		console.error("Error fetching page content:", error);
	}
};

onMounted(() => {
	fetchPageContent();
});

</script>


<style lang="scss" scoped>
@import "@/assets/scss/app";

@each $lang in $languages {
  html[lang="#{$lang}"] {
    .portfolio-sorting__item {
      @include font-size("small", $lang);
    }

    .heading {
      line-height: 1;
    }

    .heading-hightlight {
      line-height: 1.2;
    }
  }
  .section__banner {
    .heading {
      @include font-size("h1", $lang);
      @include media-breakpoint-down(sm) {
        @include font-size("h3", $lang);
      }
    }

    .heading-hightlight {
      @include font-size("h1", $lang);
      @include media-breakpoint-down(sm) {
        @include font-size("h3", $lang);
      }
    }
  }
}

.heading-hightlight {
  text-indent: 4rem;
}

.section {
  margin-top: 74px;
  padding: 5.3rem 0 5rem 0;
}

.section__banner {
  padding: 0 0 2.5rem 0;
}

.portfolio-sorting {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 3.125rem;

  .portfolio-sorting__item {
    padding: 0.5rem 1.25rem;
    border-radius: 20px;
    border: 1px solid $black;
    text-transform: uppercase;
    background-color: transparent;
    color: $black;
    cursor: pointer;
    @include transition();

    @include hover {
      background-color: $yellow-ci;
      border: 1px solid $yellow-ci;
    }

    &.active {
      background-color: $yellow-ci;
      border: 1px solid $yellow-ci;
    }
  }
}

.home {
  &.home--connect {
    color: $black;
    background: linear-gradient(
      180deg,
      rgba($yellow-ci-2, 0) 0%,
      $yellow-ci 66.5%,
      $orange 99.99%,
      $orange 100%
    );
    .home__circle {
      border: 1px solid $black;
      color: $black;
    }
  }
}
</style>
